import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useMemo } from "react";
import { useFragment } from "react-relay";

import { selectedRoundIdAtom } from "@/screens/JoinBooking/ConfirmPanel/atoms/stores";
import {
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { JoiningUserRow } from "../JoiningUserRow/JoiningUserRow";
import {
  SelectedCircle,
  UnselectedCircle,
} from "../SelectionCircles/SelectionCircles";
import { SelectActivityManagementRound_Booking$key } from "./__generated__/SelectActivityManagementRound_Booking.graphql";

export const SelectActivityManagementRound = ({
  bookingRef,
  firstName,
  lastName,
}: {
  bookingRef: SelectActivityManagementRound_Booking$key | null;
  firstName?: string;
  lastName?: string;
}) => {
  const booking = useFragment(
    SelectActivityManagementRoundFragmentString,
    bookingRef
  );
  /**
   * get a list of rounds with no player on them.
   * Then filter out any duplicates so we offer only
   * unique options to the user.
   */
  const joinableRounds = useMemo(
    () =>
      booking?.rounds?.edges
        ?.filter((round) => !round?.node?.player?.id)
        ?.flatMap((round) => (round?.node ? [round.node] : []))
        ?.filter(
          (round, index, self) =>
            index ===
            self.findIndex(
              (r) => r.roundRate?.rate?.id === round.roundRate?.rate?.id
            )
        ) ?? [],
    [booking]
  );

  const allPlayerRatesAreNull = useMemo(() => {
    return joinableRounds.every((round) => !round.roundRate?.rate?.id);
  }, [joinableRounds]);

  const selectedRoundId = useAtomValue(selectedRoundIdAtom);
  const setSelectedRoundId = useSetAtom(selectedRoundIdAtom);

  useEffect(() => {
    if (joinableRounds.length > 0 && selectedRoundId === null) {
      setSelectedRoundId(joinableRounds[0].id);
    }
  }, [joinableRounds, selectedRoundId, setSelectedRoundId]);

  return (
    <VStack gap={4}>
      <JoiningUserRow
        firstName={firstName ?? null}
        lastName={lastName ?? null}
      />
      <VStack>
        {!allPlayerRatesAreNull
          ? joinableRounds.map((round) => (
              <Pressable
                key={round.id}
                paddingVertical={3}
                onPress={() => setSelectedRoundId(round.id)}
              >
                <HStack alignItems="center">
                  <HStack gap={3} alignItems="center">
                    {round.id === selectedRoundId ? (
                      <SelectedCircle />
                    ) : (
                      <UnselectedCircle />
                    )}
                    <Text variant="mainSmallSemiBold">
                      {round?.roundRate?.rate?.name}
                    </Text>
                  </HStack>
                </HStack>
              </Pressable>
            ))
          : null}
      </VStack>
    </VStack>
  );
};

const SelectActivityManagementRoundFragmentString = graphql`
  fragment SelectActivityManagementRound_Booking on Booking {
    rounds {
      edges {
        node {
          id
          roundRate {
            rate {
              id
              name
            }
          }
          player {
            id
          }
        }
      }
    }
  }
`;
